import React, { useState, useRef, useEffect } from 'react';

const COMMANDS = {
  help: () => `Available commands:
  help     - Show this help message
  cls      - Clear the screen
  dir      - List directory contents
  ver      - Show version information
  date     - Display current date
  time     - Display current time
  echo     - Display a message
  about    - About Autonomous
  matrix   - Enter the Matrix
  exit     - Close command prompt`,
  
  cls: 'CLEAR',
  
  dir: () => ` Volume in drive C is AUTONOMOUS
 Volume Serial Number is 1337-AI95

 Directory of C:\\AUTONOMOUS

.              <DIR>         01-15-26  12:00a
..             <DIR>         01-15-26  12:00a
SERVICES   EXE     42,069    01-15-26  12:00a
METRICS    SYS     13,370    01-15-26  12:00a
ABOUT      TXT      8,088    01-15-26  12:00a
CONTACT    MSG      4,096    01-15-26  12:00a
README     TXT     16,384    01-15-26  12:00a
        5 file(s)         84,007 bytes
        2 dir(s)    1,337,000 bytes free`,
  
  ver: () => `\nAutonomous OS [Version 1.0.2025]\n(c) 2025 Autonomous AS. All rights reserved.\n`,
  
  date: () => `Current date is ${new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' })}`,
  
  time: () => `Current time is ${new Date().toLocaleTimeString()}`,
  
  about: () => `
╔══════════════════════════════════════════════════════════╗
║                    AUTONOMOUS AS                          ║
║                   Oslo, Norway 2025                       ║
╠══════════════════════════════════════════════════════════╣
║  We build AI systems that WORK.                          ║
║                                                          ║
║  → Run autonomously (no babysitting required)            ║
║  → Ship in weeks (not quarters)                          ║
║  → Stay reliable (99.7% uptime)                          ║
║  → Actually solve problems                               ║
╚══════════════════════════════════════════════════════════╝`,
  
  matrix: 'MATRIX',
  exit: 'EXIT'
};

export default function CommandPrompt({ onMatrix, onClose }) {
  const [history, setHistory] = useState([
    'Autonomous OS [Version 1.0.2025]',
    '(c) 2025 Autonomous AS. All rights reserved.',
    '',
    'Type "help" for available commands.',
    ''
  ]);
  const [input, setInput] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef(null);
  const outputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history]);

  const executeCommand = (cmd) => {
    const parts = cmd.trim().toLowerCase().split(' ');
    const command = parts[0];
    const args = parts.slice(1).join(' ');

    if (!command) return '';

    if (command === 'echo') {
      return args || '';
    }

    const handler = COMMANDS[command];
    if (!handler) {
      return `'${command}' is not recognized as an internal or external command,\noperable program or batch file.`;
    }

    if (handler === 'CLEAR') {
      setHistory([]);
      return null;
    }

    if (handler === 'MATRIX') {
      onMatrix?.();
      return 'Entering the Matrix...';
    }

    if (handler === 'EXIT') {
      onClose?.();
      return null;
    }

    return typeof handler === 'function' ? handler() : handler;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newHistory = [...history, `C:\\AUTONOMOUS> ${input}`];
    const result = executeCommand(input);
    if (result !== null) {
      newHistory.push(result, '');
    }
    setHistory(newHistory);
    setCommandHistory(prev => [input, ...prev]);
    setInput('');
    setHistoryIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyIndex < commandHistory.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  };

  return (
    <div className="command-prompt" onClick={() => inputRef.current?.focus()}>
      <div className="command-output" ref={outputRef}>
        {history.map((line, i) => (
          <div key={i} className="command-line">{line}</div>
        ))}
      </div>
      <form onSubmit={handleSubmit} className="command-input-line">
        <span>C:\AUTONOMOUS&gt; </span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className="command-input"
          autoComplete="off"
          spellCheck="false"
        />
      </form>
    </div>
  );
}
